// The interactive canvas stack: tape, gutter, heat, crosshair overlay and
// tooltip. Owns the view window and the pan/zoom gestures; everything it
// draws comes from render/.

import { MISSING, type Run } from '../model.ts'
import {
  describeFocus,
  drawCheckChart,
  drawCheckColumn,
  drawEdges,
  drawFilterEdges,
  drawStrips,
  layoutStrips,
  stripsHeight,
  type CheckFocus,
  type CheckOverlay,
  type StripLayout,
  type VisibleScheme,
} from '../overlays/draw.ts'
import { buildHeatMask, insertionLossDb, maskedDb, type ResolvedFilter } from '../overlays/filter.ts'
import { scoreAt, type CollisionProfile } from '../overlays/fit.ts'
import { ColorTable, paintToContext } from '../render/heat.ts'
import { drawGutter, footerLines, GUTTER_WIDTH, layoutTimeTicks } from '../render/gutter.ts'
import {
  clampView,
  clipCell,
  colToHz,
  fullView,
  hzToCol,
  panView,
  rowTimeMs,
  rowToMs,
  timeToRow,
  xToCol,
  yToRow,
  zoomView,
  type Clip,
  type View,
} from '../render/layout.ts'
import type { PaletteName } from '../render/palette.ts'
import { drawTape, layoutTape, TAPE_HEIGHT } from '../render/tape.ts'
import { Pyramid } from '../render/tiles.ts'
import { formatClock } from '../tz.ts'
import { formatMHz } from '../units.ts'
import type { ViewWindow } from '../url.ts'

export interface ViewerElements {
  tape: HTMLCanvasElement
  stripsRow: HTMLElement
  strips: HTMLCanvasElement
  gutter: HTMLCanvasElement
  heatWrap: HTMLElement
  heat: HTMLCanvasElement
  overlay: HTMLCanvasElement
  tooltip: HTMLElement
  footer: HTMLElement
}

export interface Display {
  palette: PaletteName
  displayZone: string
  ytickSec: number | null
  /** Already resolved against the run; see `clampDbRange`. */
  dbMin: number
  dbMax: number
  /** Schemes drawn as strips and edges, in stacking order. */
  schemes: VisibleScheme[]
  /**
   * The signal check, or null when it is off: the scored profile for the chart
   * and the footprint the pointer's column takes. Absent while a fresh profile
   * is still being computed, the chart waits rather than showing a stale one.
   */
  check: { profile: CollisionProfile | null; widthHz: number; airtimeS: number } | null
  /** The pinned centers, in list order, drawn in their own color and numbered. */
  pins: readonly { centerHz: number; startMs: number }[]
  /** A receive filter applied to the readings before they are colored, or null. */
  filter: ResolvedFilter | null
}

const MAX_DPR = 2
const FULL_QUALITY_DELAY_MS = 120
/** Movement past this many CSS pixels makes a press a drag rather than a tap. */
const TAP_SLOP_PX = 8
/** Fingers closer than this give a distance ratio that is mostly noise. */
const MIN_PINCH_PX = 24

export class Viewer {
  readonly #el: ViewerElements
  readonly #colors = new ColorTable()
  #clip: Clip | null = null
  #pyramid: Pyramid | null = null
  #view: View | null = null
  #display: Display
  #frame = 0
  #fast = false
  #fullTimer = 0
  #pointer: { x: number; y: number } | null = null
  /** Whether the pointer being read is a finger, which covers what is under it. */
  #touching = false
  #drag: { x: number; y: number; view: View } | null = null
  /** Every pointer currently down, in CSS pixels on the heat, keyed by id. */
  readonly #pointers = new Map<number, { x: number; y: number }>()
  /** A two-finger gesture, baselined at the moment the second finger landed. */
  #pinch: { view: View; distance: number; fx: number; fy: number } | null = null
  /** The press in progress, for deciding whether a release was a tap. */
  #press: { x: number; y: number; moved: boolean; multi: boolean } | null = null
  /** Strip layouts for the view last rendered; the overlay reuses them between renders. */
  #stripLayouts: StripLayout[] = []
  #placing = false
  #onPin: (hz: number, ms: number) => void = () => {}
  /** Where a tap at a frequency actually lands, e.g. the nearest channel of a visible scheme. */
  #snapHz: (hz: number) => number = (hz) => hz
  #onViewChange: () => void = () => {}

  constructor(elements: ViewerElements, display: Display) {
    this.#el = elements
    this.#display = display
    this.#bind()
    new ResizeObserver(() => this.requestRender()).observe(elements.heatWrap)
  }

  get clip(): Clip | null {
    return this.#clip
  }
  get pyramid(): Pyramid | null {
    return this.#pyramid
  }
  get view(): View | null {
    return this.#view
  }

  onViewChange(handler: () => void): void {
    this.#onViewChange = handler
  }

  /** Called with the frequency and instant under a click while placement mode is on. */
  onPin(handler: (hz: number, ms: number) => void): void {
    this.#onPin = handler
  }

  /** Snapping applies to the pointer's column and the pin alike, so what you see is what lands. */
  setSnap(snap: (hz: number) => number): void {
    this.#snapHz = snap
  }

  get placing(): boolean {
    return this.#placing
  }

  /**
   * Placement mode: the next click puts a pin down. A mode rather than a bare
   * click, because the pointer is already busy reading the chart everywhere it
   * goes, and a reading gesture that also leaves things behind would drop a
   * pin every time someone looked at the plot.
   */
  setPlacementMode(on: boolean): void {
    this.#placing = on
    this.#el.heatWrap.classList.toggle('placing', on)
    this.#refresh()
  }

  /** The center and score under pointer (x, y), or of a pinned center, while the check is on. */
  #focusAt(clip: Clip, view: View, x: number, y: number, width: number, height: number): CheckFocus | null {
    const check = this.#display.check
    if (!check) return null
    const centerHz = this.#snapHz(colToHz(clip, xToCol(view, x, width)))
    return this.#focusFor(clip, centerHz, rowToMs(clip, yToRow(view, y, height)))
  }

  #focusFor(clip: Clip, centerHz: number, startMs: number): CheckFocus | null {
    const check = this.#display.check
    if (!check) return null
    const filter = this.#display.filter
    return {
      centerHz,
      startMs,
      score: check.profile ? scoreAt(check.profile, clip, centerHz) : NaN,
      lossDb: filter ? insertionLossDb(centerHz, check.widthHz, filter) : 0,
    }
  }

  /** Installs a new clip; the view resets unless the run is the same and the old view still fits. */
  setClip(clip: Clip | null, keepView = true): void {
    const previousRun: Run | null = this.#clip?.run ?? null
    this.#clip = clip
    this.#pyramid = clip ? new Pyramid(clip) : null
    if (!clip) {
      this.#view = null
    } else if (keepView && this.#view && previousRun === clip.run) {
      this.#view = clampView(clip, this.#view)
    } else {
      this.#view = fullView(clip)
    }
    this.requestRender()
    this.#onViewChange()
  }

  setDisplay(display: Display): void {
    this.#display = display
    this.requestRender()
  }

  resetView(): void {
    if (!this.#clip) return
    this.#view = fullView(this.#clip)
    this.requestRender()
    this.#onViewChange()
  }

  /**
   * The window on screen as frequency and time rather than clip coordinates,
   * so it survives being written down and read back against a different crop.
   */
  get window(): ViewWindow | null {
    const clip = this.#clip
    const view = this.#view
    if (!clip || !view) return null
    return {
      lowHz: colToHz(clip, view.col0),
      highHz: colToHz(clip, view.col1),
      startMs: rowToMs(clip, view.row0),
      endMs: rowToMs(clip, view.row1),
    }
  }

  /** Puts a frequency and time window on screen, clamped to the clip. */
  showWindow(window: ViewWindow): void {
    const clip = this.#clip
    if (!clip) return
    this.#view = clampView(clip, {
      col0: hzToCol(clip, window.lowHz),
      col1: hzToCol(clip, window.highHz),
      row0: timeToRow(clip, window.startMs),
      row1: timeToRow(clip, window.endMs),
    })
    this.requestRender()
    this.#onViewChange()
  }

  /**
   * Whether the whole clip is on screen. A shared link leaves the window out
   * when it is, so an unzoomed view shares as a short URL and stays correct
   * for a recipient whose crop resolves to a different number of bins.
   */
  get full(): boolean {
    const clip = this.#clip
    const view = this.#view
    if (!clip || !view) return true
    const whole = fullView(clip)
    const dx = (whole.col1 - whole.col0) * 1e-4
    const dy = (whole.row1 - whole.row0) * 1e-4
    return (
      Math.abs(view.col0 - whole.col0) < dx &&
      Math.abs(view.col1 - whole.col1) < dx &&
      Math.abs(view.row0 - whole.row0) < dy &&
      Math.abs(view.row1 - whole.row1) < dy
    )
  }

  /** Pans horizontally, keeping the zoom, until `hz` is inside the view. */
  revealHz(hz: number): void {
    const clip = this.#clip
    const view = this.#view
    if (!clip || !view) return
    const col = hzToCol(clip, hz)
    const width = view.col1 - view.col0
    if (col >= view.col0 && col <= view.col1) return
    this.#view = panView(clip, view, col - width / 2 - view.col0, 0)
    this.requestRender()
    this.#onViewChange()
  }

  requestRender(): void {
    if (this.#frame) return
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0
      this.#render()
    })
  }

  #interacting(): void {
    this.#fast = true
    clearTimeout(this.#fullTimer)
    this.#fullTimer = window.setTimeout(() => {
      this.#fast = false
      this.requestRender()
    }, FULL_QUALITY_DELAY_MS)
  }

  #size(canvas: HTMLCanvasElement, cssWidth: number, cssHeight: number, dpr: number): CanvasRenderingContext2D {
    const w = Math.max(1, Math.round(cssWidth * dpr))
    const h = Math.max(1, Math.round(cssHeight * dpr))
    if (canvas.width !== w) canvas.width = w
    if (canvas.height !== h) canvas.height = h
    return canvas.getContext('2d')!
  }

  #render(): void {
    const el = this.#el
    const cssWidth = el.heatWrap.clientWidth
    const cssHeight = el.heatWrap.clientHeight
    if (cssWidth < 2 || cssHeight < 2) return
    const clip = this.#clip
    const view = this.#view
    const dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR)

    if (!clip || !view || !this.#pyramid) {
      const heat = this.#size(el.heat, cssWidth, cssHeight, 1)
      heat.fillStyle = '#2a2a32'
      heat.fillRect(0, 0, el.heat.width, el.heat.height)
      this.#size(el.tape, cssWidth, TAPE_HEIGHT, dpr).clearRect(0, 0, el.tape.width, el.tape.height)
      this.#size(el.gutter, GUTTER_WIDTH, cssHeight, dpr).clearRect(0, 0, el.gutter.width, el.gutter.height)
      this.#size(el.overlay, cssWidth, cssHeight, dpr).clearRect(0, 0, el.overlay.width, el.overlay.height)
      el.stripsRow.hidden = true
      this.#stripLayouts = []
      el.footer.textContent = ''
      return
    }

    // Heat at reduced resolution while a gesture is in progress.
    const heatDpr = this.#fast ? 1 : dpr
    const heat = this.#size(el.heat, cssWidth, cssHeight, heatDpr)
    const lut = this.#colors.update(this.#display.palette, this.#display.dbMin, this.#display.dbMax)
    // The mask is sampled per device pixel column, so it is built against the
    // canvas the painter writes rather than the CSS box the overlay draws in.
    const filter = this.#display.filter
    const mask = filter ? buildHeatMask(clip, view, el.heat.width, filter) : null
    paintToContext(heat, this.#pyramid, view, lut, el.heat.width, el.heat.height, mask)

    const tape = this.#size(el.tape, cssWidth, TAPE_HEIGHT, dpr)
    drawTape(tape, layoutTape(clip, view, cssWidth), cssWidth, dpr)

    // Scheme strips under the tape; the row collapses when none are visible.
    const schemes = this.#display.schemes
    const stripsPx = stripsHeight(schemes.length)
    el.stripsRow.hidden = stripsPx === 0
    if (stripsPx > 0) {
      el.stripsRow.style.height = `${stripsPx}px`
      const strips = this.#size(el.strips, cssWidth, stripsPx, dpr)
      strips.font = '10px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif'
      const measure = (text: string) => strips.measureText(text).width
      this.#stripLayouts = layoutStrips(schemes, clip, view, cssWidth, measure)
      drawStrips(strips, this.#stripLayouts, cssWidth, dpr)
    } else {
      this.#stripLayouts = []
    }

    const gutter = this.#size(el.gutter, GUTTER_WIDTH, cssHeight, dpr)
    const ticks = layoutTimeTicks(clip, view, cssHeight, this.#display.displayZone, this.#display.ytickSec)
    drawGutter(gutter, ticks, GUTTER_WIDTH, cssHeight, dpr)

    const lines = footerLines(clip, this.#display.displayZone)
    el.footer.textContent = `${lines.duration}   ${lines.range}   ${lines.pixel}   ${lines.started}`

    this.#drawOverlay(dpr)
  }

  #drawOverlay(dpr: number): void {
    const el = this.#el
    const cssWidth = el.heatWrap.clientWidth
    const cssHeight = el.heatWrap.clientHeight
    const ctx = this.#size(el.overlay, cssWidth, cssHeight, dpr)
    ctx.clearRect(0, 0, el.overlay.width, el.overlay.height)
    const p = this.#pointer
    const clip = this.#clip
    const view = this.#view
    if (!clip || !view) {
      el.tooltip.hidden = true
      return
    }
    const check = this.#display.check
    // Nothing to read while the view itself is moving.
    const reading = p !== null && !this.#drag && !this.#pinch
    const focus = reading && check ? this.#focusAt(clip, view, p.x, p.y, cssWidth, cssHeight) : null
    const pins = check
      ? this.#display.pins.map((pin) => this.#focusFor(clip, pin.centerHz, pin.startMs)!).filter(Boolean)
      : []

    // Columns first: they tint the plot, everything else sits on it.
    pins.forEach((pin, i) => {
      if (check) drawCheckColumn(ctx, clip, view, pin, check, cssWidth, cssHeight, dpr, true, String(i + 1))
    })
    if (check && focus) drawCheckColumn(ctx, clip, view, focus, check, cssWidth, cssHeight, dpr, false)
    if (this.#display.filter) drawFilterEdges(ctx, clip, view, this.#display.filter, cssWidth, cssHeight, dpr)
    if (this.#stripLayouts.length > 0) drawEdges(ctx, this.#stripLayouts, cssHeight, dpr)
    // Last, so the chart is never drawn over: it is opaque and owns its strip.
    if (check?.profile) {
      const overlay: CheckOverlay = {
        scores: check.profile.scores,
        bin0: check.profile.bin0,
        widthHz: check.widthHz,
        airtimeS: check.airtimeS,
        budget: check.profile.budget,
        peak: check.profile.peak,
      }
      drawCheckChart(ctx, clip, view, overlay, focus, pins, cssWidth, cssHeight, dpr)
    }
    if (!reading) {
      el.tooltip.hidden = true
      return
    }
    ctx.save()
    ctx.scale(dpr, dpr)
    ctx.strokeStyle = 'rgba(255,255,255,0.7)'
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(p.x + 0.5, 0)
    ctx.lineTo(p.x + 0.5, cssHeight)
    ctx.moveTo(0, p.y + 0.5)
    ctx.lineTo(cssWidth, p.y + 0.5)
    ctx.stroke()
    ctx.restore()

    const col = xToCol(view, p.x, cssWidth)
    const row = yToRow(view, p.y, cssHeight)
    const bin = Math.round(col)
    const r = Math.floor(row + 0.5)
    const centi = clipCell(clip, r, bin)
    const hz = colToHz(clip, bin)
    const timeText = formatClock(rowTimeMs(clip, r), this.#display.displayZone, 'full')
    const value = centi === MISSING ? 'no data' : `${(centi / 100).toFixed(2)} dB`
    // With a filter on, the reading and the pixel disagree; the tooltip says both.
    const filter = this.#display.filter
    const through =
      filter && centi !== MISSING ? `\nthrough filter ${maskedDb(clip, bin, centi / 100, filter).toFixed(2)} dB` : ''
    const signal = focus ? `\nsignal ${describeFocus(focus, clip.run.geometry.stepHz)}` : ''
    el.tooltip.textContent = `${formatMHz(hz, clip.run.geometry.stepHz)}\n${timeText}\n${value}${through}${signal}`
    el.tooltip.hidden = false
    const tw = el.tooltip.offsetWidth
    const th = el.tooltip.offsetHeight
    // Beside a mouse pointer; above a finger, which would otherwise cover it.
    // Clamped to the plot either way, since a narrow screen can hold the
    // readout only by pushing it back inside.
    const clamp = (v: number, max: number) => Math.max(0, Math.min(v, max))
    if (this.#touching) {
      el.tooltip.style.left = `${clamp(p.x - tw / 2, cssWidth - tw)}px`
      el.tooltip.style.top = `${clamp(p.y - 40 - th, cssHeight - th)}px`
    } else {
      el.tooltip.style.left = `${clamp(p.x + 14 + tw > cssWidth ? p.x - 14 - tw : p.x + 14, cssWidth - tw)}px`
      el.tooltip.style.top = `${clamp(p.y + 14 + th > cssHeight ? p.y - 14 - th : p.y + 14, cssHeight - th)}px`
    }
  }

  /** Pointer position in CSS pixels on the heat. */
  #local(event: PointerEvent): { x: number; y: number } {
    const rect = this.#el.heatWrap.getBoundingClientRect()
    return { x: event.clientX - rect.left, y: event.clientY - rect.top }
  }

  #refresh(): void {
    this.#drawOverlay(Math.min(window.devicePixelRatio || 1, MAX_DPR))
  }

  /** The two fingers' separation and midpoint, as a fraction of the canvas. */
  #span(): { distance: number; fx: number; fy: number } | null {
    const [a, b] = [...this.#pointers.values()]
    if (!a || !b) return null
    const width = this.#el.heatWrap.clientWidth
    const height = this.#el.heatWrap.clientHeight
    if (width < 2 || height < 2) return null
    return {
      distance: Math.max(MIN_PINCH_PX, Math.hypot(a.x - b.x, a.y - b.y)),
      fx: (a.x + b.x) / 2 / width,
      fy: (a.y + b.y) / 2 / height,
    }
  }

  #beginPinch(): void {
    const view = this.#view
    const span = this.#span()
    if (!view || !span) return
    this.#pinch = { view, ...span }
  }

  /**
   * Zoom and pan in one gesture, measured against the view the pinch started
   * from rather than the last frame: the clip coordinate the fingers came down
   * around stays under the fingers, so a pinch that returns to where it began
   * returns the view with it, and a long gesture accumulates no drift.
   *
   * Both axes scale together, as a wheel without modifiers does. Deriving the
   * axes separately from the horizontal and vertical components would let a
   * pinch stretch time alone, but two fingers nearly in line give one component
   * almost no length, and that ratio is noise.
   */
  #applyPinch(): void {
    const clip = this.#clip
    const pinch = this.#pinch
    const span = this.#span()
    if (!clip || !pinch || !span) return

    const scale = span.distance / pinch.distance
    const cols = (pinch.view.col1 - pinch.view.col0) / scale
    const rows = (pinch.view.row1 - pinch.view.row0) / scale
    const anchorCol = pinch.view.col0 + pinch.fx * (pinch.view.col1 - pinch.view.col0)
    const anchorRow = pinch.view.row0 + pinch.fy * (pinch.view.row1 - pinch.view.row0)
    const col0 = anchorCol - span.fx * cols
    const row0 = anchorRow - span.fy * rows

    this.#view = clampView(clip, { col0, col1: col0 + cols, row0, row1: row0 + rows })
    this.#interacting()
    this.requestRender()
    this.#onViewChange()
  }

  #bind(): void {
    const wrap = this.#el.heatWrap

    wrap.addEventListener(
      'wheel',
      (event) => {
        if (!this.#clip || !this.#view) return
        event.preventDefault()
        const rect = wrap.getBoundingClientRect()
        const fx = (event.clientX - rect.left) / rect.width
        const fy = (event.clientY - rect.top) / rect.height
        const delta = event.deltaMode === 1 ? event.deltaY * 16 : event.deltaY
        const factor = Math.exp(delta * 0.002)
        const fxZoom = event.altKey ? 1 : factor
        const fyZoom = event.shiftKey ? 1 : factor
        this.#view = zoomView(this.#clip, this.#view, fx, fy, fxZoom, fyZoom)
        this.#interacting()
        this.requestRender()
        this.#onViewChange()
      },
      { passive: false },
    )

    // A mouse drags to pan and hovers to read. A finger cannot do both at once,
    // so touch splits them: one finger is the crosshair, two are the view.
    // Panning under the same finger that reads would move the thing being read.
    wrap.addEventListener('pointerdown', (event) => {
      if (!this.#view) return
      if (event.pointerType === 'mouse' && event.button !== 0) return
      wrap.setPointerCapture(event.pointerId)
      const at = this.#local(event)
      this.#pointers.set(event.pointerId, at)
      this.#pointer = at
      this.#touching = event.pointerType === 'touch'

      if (this.#pointers.size === 1) {
        this.#press = { x: event.clientX, y: event.clientY, moved: false, multi: false }
        if (event.pointerType !== 'touch') {
          this.#drag = { x: event.clientX, y: event.clientY, view: this.#view }
          wrap.classList.add('panning')
        }
      } else if (this.#pointers.size === 2) {
        if (this.#press) this.#press.multi = true
        this.#drag = null
        wrap.classList.remove('panning')
        this.#beginPinch()
      }
      this.#refresh()
    })

    wrap.addEventListener('pointermove', (event) => {
      const at = this.#local(event)
      this.#pointer = at
      this.#touching = event.pointerType === 'touch'
      if (this.#pointers.has(event.pointerId)) this.#pointers.set(event.pointerId, at)
      const press = this.#press
      if (press && Math.abs(event.clientX - press.x) + Math.abs(event.clientY - press.y) > TAP_SLOP_PX) {
        press.moved = true
      }

      if (this.#pinch) {
        this.#applyPinch()
        return
      }
      const drag = this.#drag
      if (drag && this.#clip) {
        const rect = wrap.getBoundingClientRect()
        const cols = ((drag.view.col1 - drag.view.col0) * -(event.clientX - drag.x)) / rect.width
        const rows = ((drag.view.row1 - drag.view.row0) * -(event.clientY - drag.y)) / rect.height
        this.#view = panView(this.#clip, drag.view, cols, rows)
        this.#interacting()
        this.requestRender()
        this.#onViewChange()
        return
      }
      this.#refresh()
    })

    const release = (event: PointerEvent, tap: boolean) => {
      if (!this.#pointers.delete(event.pointerId)) return
      if (wrap.hasPointerCapture(event.pointerId)) wrap.releasePointerCapture(event.pointerId)

      if (this.#pointers.size >= 2) {
        // Still a pinch; re-baseline so the view does not jump as a finger goes.
        this.#beginPinch()
        return
      }
      this.#pinch = null
      if (this.#pointers.size === 1) {
        // Back to one finger: it points again, and its own landing position is
        // the baseline, so the view does not lurch to wherever it drifted.
        const [remaining] = [...this.#pointers.values()]
        if (remaining) this.#pointer = remaining
        this.#refresh()
        return
      }

      const press = this.#press
      this.#press = null
      this.#drag = null
      wrap.classList.remove('panning')
      // A click puts a pin down while placement mode is on; a drag still pans,
      // so the mode only changes what a click does.
      if (tap && press && !press.moved && !press.multi && this.#placing && this.#display.check && this.#clip && this.#view) {
        const rect = wrap.getBoundingClientRect()
        const focus = this.#focusAt(this.#clip, this.#view, event.clientX - rect.left, event.clientY - rect.top, rect.width, rect.height)
        if (focus) this.#onPin(focus.centerHz, focus.startMs)
      }
      // A finger that has lifted is not hovering; a mouse still is.
      if (event.pointerType === 'touch') this.#pointer = null
      this.#refresh()
    }
    wrap.addEventListener('pointerup', (event) => release(event, true))
    wrap.addEventListener('pointercancel', (event) => release(event, false))

    wrap.addEventListener('pointerleave', (event) => {
      if (this.#pointers.has(event.pointerId)) return
      this.#pointer = null
      this.#refresh()
    })

    wrap.addEventListener('dblclick', () => this.resetView())
  }
}
